import { MagicModel } from '@/types/MagicModel';
import { GeneralSettingsType } from '../SettingsSidebar/types';
import {
  SensorButtonNodeType,
  SensorBuzzerNodeType,
  SensorTrihowSpecificNodeType,
} from '../Nodes/types';

// Logic to generate options options for different sensors based on already selected sensors
// Used in the corresponding settings sidebar components
export const generateButtonOptions = (
  languageSelective: boolean,
  buttons: GeneralSettingsType['buttons'],
  selectedButtons: string[],
  currentTriggerIds: { triggerId: string; name: string }[],
  languageAnyButtons: SensorButtonNodeType[],
): { label: string | null; value: string }[] => {
  const identifierOffset: Record<string, number> = { A: 0, B: 1, C: 2, D: 3 };

  const sortedButtons = buttons.sort(
    (a, b) => Number(a.channel) - Number(b.channel),
  );

  const buttonOptions: { label: string | null; value: string }[] = [];
  let currentChannel: string | null = null;
  let baseValue = 21;

  for (const button of sortedButtons) {
    if (button.buttonIdentifier) {
      if (button.channel !== currentChannel) {
        currentChannel = button.channel;
        // Increment baseValue for each new channel, except the first
        if (buttonOptions.length > 0) {
          baseValue += 4;
        }
      }
      const offset = identifierOffset[button.buttonIdentifier];
      if (offset !== undefined) {
        buttonOptions.push({
          label: button.name,
          value: (baseValue + offset).toString(),
        });
      }
    }
  }

  // Only filter options if languageSelective is inactive
  return languageSelective
    ? buttonOptions.filter(
        (btn) =>
          !languageAnyButtons.some((b) =>
            b.data.options.buttons?.some(
              (button) => button.triggerId === btn.value,
            ),
          ),
      )
    : buttonOptions.filter(
        (button) =>
          !selectedButtons.includes(button.value) ||
          currentTriggerIds.some(
            (current) => current.triggerId === button.value,
          ),
      );
};

export const generateSpecificTrihowOptions = (
  magicModels: MagicModel[],
  selectedTrihows: string[],
  currentTrihows:
    | {
        triggerId: string;
        name: string;
      }[]
    | null,
  languageSelective: boolean,
  languageAnyTrihows: SensorTrihowSpecificNodeType[],
) => {
  // Only filter options if languageSelective is inactive
  return languageSelective
    ? magicModels
        .map((model) => ({
          label: model.modelName,
          value: model.sensorId,
        }))
        .filter(
          (trihow) =>
            !languageAnyTrihows.some((t) =>
              t.data.options.trihow?.some(
                (th) => th.triggerId === trihow.value,
              ),
            ),
        )
    : magicModels
        .map((model) => ({
          label: model.modelName,
          value: model.sensorId,
        }))
        .filter(
          (trihow) =>
            !selectedTrihows.includes(trihow.value) ||
            currentTrihows?.some(
              (current) => current.triggerId === trihow.value,
            ),
        );
};

export const generateSpecificTrihowChannelOptions = (
  nfcReaders: GeneralSettingsType['nfcReaders'],
) => {
  return nfcReaders.map((_, index) => ({
    value: index + 1,
    label: index + 1,
  }));
};

export const generateBuzzerOptions = (
  languageSelective: boolean,
  buzzers: GeneralSettingsType['buzzers'],
  selectedBuzzers: string[],
  currentTriggerIds: { triggerId: string; name: string }[],
  languageAnyBuzzers: SensorBuzzerNodeType[],
): { label: string | null; value: string }[] => {
  // Only filter options if languageSelective is inactive
  return languageSelective
    ? buzzers
        .map((buzzer) => ({
          label: buzzer.name,
          value: buzzer.channel ?? '',
        }))
        .filter(
          (buzzer) =>
            !languageAnyBuzzers.some((b) =>
              b.data.options.buzzers?.some(
                (bzz) => bzz.triggerId === buzzer.value,
              ),
            ),
        )
    : buzzers
        .map((buzzer) => ({
          label: buzzer.name,
          value: buzzer.channel ?? '',
        }))
        .filter(
          (buzzer) =>
            !selectedBuzzers.includes(buzzer.value) ||
            currentTriggerIds.some(
              (current) => current.triggerId === buzzer.value,
            ),
        );
};

export const generateLEDOptions = (
  leds: GeneralSettingsType['leds'],
): {
  label: string | null;
  value: {
    channel: string | null;
    identifier: string | null;
    name: string | null;
  };
}[] => {
  return leds.map((led) => ({
    label: led.name,
    value: {
      channel: led.channel,
      identifier: led.ledIdentifier,
      name: led.name,
    },
  }));
};
